import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Button, TextField, Grid, Typography, Box, Modal, Snackbar, makeStyles, Divider,
  TableContainer, Table, TableHead, TableBody, TableRow, TableCell,
} from '@material-ui/core';
import { Stack } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import moment from 'moment';
import { api_call, curl_post, empty, getToken } from '../helpers/untils';
import { API_BASE_URL } from '../config/constant';
import { layoutActions } from '../store';
import { setSettingPersist } from '../helpers/misc';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '90%',
  maxWidth: '1120px',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 3,
};
const useStyles = makeStyles(() => ({
  buttons: {
    display: 'flex',
    justifyContent: 'space-evenly',
    '& > *': {
      flexBasis: '48%',
    },
  },
  formContainer: {
    display: 'block',
    width: '100%',
  },
}));

const formatDateTime = (value) => {
  if (empty(value)) {
    return '';
  }
  const m = moment(value);
  return m.isValid() ? m.format('LLL') : String(value);
};

const ChargingHistoryTable = ({ records, onDownloadInvoice }) => (
  <TableContainer>
    <Table>
      <TableHead>
        <TableRow>
          <TableCell>Vin</TableCell>
          <TableCell>Charge Start</TableCell>
          <TableCell>Charge Stop</TableCell>
          <TableCell>Location</TableCell>
          <TableCell>Invoices</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {records.map((record, index) => (
          <TableRow key={record?.id ?? index}>
            <TableCell>{record?.vin}</TableCell>
            <TableCell>{formatDateTime(record?.chargeStartDateTime)}</TableCell>
            <TableCell>{formatDateTime(record?.chargeStopDateTime)}</TableCell>
            <TableCell>{record?.siteLocationName}</TableCell>
            <TableCell>
              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                {(record?.invoices || []).map((invoice, invoiceIndex) => (
                  <Button
                    key={invoice?.contentId ?? invoiceIndex}
                    type="button"
                    size="small"
                    color="primary"
                    variant="outlined"
                    endIcon={<DownloadIcon fontSize="small" />}
                    onClick={() => onDownloadInvoice(invoice?.contentId, invoice?.fileName)}
                  >
                    Download
                    {/* {invoice?.fileName || 'Download'} */}
                  </Button>
                ))}
              </Stack>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

const ChargingHistoryModal = (props) => {
  const dispatch = useDispatch();
  const { open, setOpen, deviceItem, history } = props;
  const classes = useStyles();

  const [from, setFrom] = useState(moment().subtract(30, 'd'));
  const [to, setTo] = useState(moment());
  const [records, setRecords] = useState([]);
  const [apiLoading, setApiLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const showToast = (msg) => {
    setToastMessage(msg);
    setSnackbarOpen(true);
  };

  const handleModalClose = () => {
    setOpen(false);
  };

  const handleSearch = async () => {
    const vin = deviceItem?.vin;
    if (empty(vin)) {
      console.log(`handleSearch deviceItem:::`, deviceItem)
      showToast('No VIN found on this device');
      return;
    }
    setApiLoading(true);
    const startTime = encodeURIComponent(from.format());
    const endTime = encodeURIComponent(to.format());
    const url = `/api/tesla/charging_history/${vin}?startTime=${startTime}&endTime=${endTime}`;
    const response = await api_call(url);
    if (response.ok) {
      setApiLoading(false);
      setSearched(true);

      const res = await response.json();
      const list = Array.isArray(res) ? res : (res?.data || res?.chargingHistory || res?.history || []);
      setRecords(Array.isArray(list) ? list : []);
      if (empty(list)) {
        showToast('No charging history found');
      }
    } else {
      const errorRes = response;
      if (errorRes?.tesla_reauthenticate_required) {
        goTeslaReauthenticatePage();
        return true;
      } else {
        setApiLoading(false);
        setSearched(true);
        showToast(errorRes?.error ?? errorRes?.message ?? 'Failed to load charging history');
      }
      setRecords([]);
    }
  };

  const onDownloadInvoice = (invoiceId, fileName) => {
    const token = getToken()
    const vin = deviceItem?.vin;
    if (empty(vin) || empty(invoiceId)) {
      return;
    }
    const url = `${API_BASE_URL}/api/tesla/charging_invoice/${vin}`;
    curl_post(url, { invoiceId, fileName: fileName, exportFileName: fileName }, 'post', "", true, token); //window.location.href = exportUrl
  };

  const goTeslaReauthenticatePage = async () => {
    try {
      const auth_type = "reauthenticate";
      const response = await api_call(`/api/tesla/get-login-url?auth_type=${auth_type}`);
      if (response.ok) {
        const res = await response.json()
        setSettingPersist('smartcar_auth_source', 'device')
        window.location.href = res.link
      } else {
        alert("Tesla reauthenticate error")
      }
    } catch (e) {
      console.log(`goTeslaReauthenticatePage error:::`, e)
      alert("Tesla reauthenticate error")
    }
  }

  //////////////////////// app modal close feature //////////////////////////////////////////
  const appModalCloseTimestamp = useSelector((state) => state.layout.appModalCloseTimestamp);
  React.useEffect(() => {
    if (appModalCloseTimestamp) {
      setOpen(false);
    }
  }, [appModalCloseTimestamp]);
  ///////////////////////////////////////////////////////////////////////////////////////////

  return (
    <Modal
      open={open}
      onClose={handleModalClose}
      aria-labelledby="parent-modal-title"
      aria-describedby="parent-modal-description"
    >
      <Box sx={modalStyle}>
        <Snackbar
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          open={snackbarOpen}
          onClose={() => { setSnackbarOpen(false); }}
          autoHideDuration={1500}
          message={toastMessage}
        />
        <Typography id="modal-modal-title" variant="h6" component="h2">Charging History</Typography>

        <Box sx={{ paddingTop: 2 }}>
          <Box sx={{ maxWidth: '400px', margin: 'auto' }}>
            <div className={classes.formContainer}>
              <Grid container spacing={1} justifyContent="flex-start">
                <Grid item xs={12} sm={12}>
                  <TextField
                    variant="filled"
                    label="From"
                    type="datetime-local"
                    value={from.format(moment.HTML5_FMT.DATETIME_LOCAL)}
                    onChange={(e) => setFrom(moment(e.target.value, moment.HTML5_FMT.DATETIME_LOCAL))}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={12}>
                  <TextField
                    variant="filled"
                    label="To"
                    type="datetime-local"
                    value={to.format(moment.HTML5_FMT.DATETIME_LOCAL)}
                    onChange={(e) => setTo(moment(e.target.value, moment.HTML5_FMT.DATETIME_LOCAL))}
                    fullWidth
                  />
                </Grid>
              </Grid>
            </div>
            <div className={classes.buttons} style={{ paddingTop: '15px', maxWidth: '400px', margin: 'auto' }}>
              <Button type="button" color="primary" variant="contained" onClick={handleSearch} disabled={apiLoading}>
                {apiLoading ? 'Please wait' : 'Search'}
              </Button>
              <Button type="button" color="primary" variant="outlined" onClick={() => setOpen(false)}>
                Close
              </Button>
            </div>
          </Box>

          {(records.length > 0) && (
            <Box sx={{ marginTop: '16px' }}>
              <Divider />
              <Box sx={{ marginTop: '16px', maxHeight: 'calc(100vh - 420px)', overflowY: 'auto' }}>
                <ChargingHistoryTable records={records} onDownloadInvoice={onDownloadInvoice} />
              </Box>
            </Box>
          )}

          {(searched && !apiLoading && records.length === 0) && (
            <Box sx={{ marginTop: '16px', textAlign: 'center' }}>
              <Typography color="textSecondary">No charging history found</Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Modal>
  );
};

export default ChargingHistoryModal;